import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { 
  getUserResumes, 
  deleteResume, 
  getUserStats, 
  getLeaderboard,
  getUserAnalytics 
} from '../services/firebaseService';
import { 
  FileText, 
  Download, 
  Eye, 
  Trophy, 
  Target, 
  Star,
  Plus,
  Calendar,
  TrendingUp,
  Award,
  LogOut,
  User,
  Settings,
  Home,
  BarChart3,
  Trash2,
  RefreshCw
} from 'lucide-react';

const DashboardReal = ({ onNavigate }) => {
  const { user, logout } = useAuth();
  const [resumes, setResumes] = useState([]);
  const [activeTab, setActiveTab] = useState('resumes');
  const [userStats, setUserStats] = useState({
    totalResumes: 0,
    totalDownloads: 0,
    averageScore: 0,
    rank: 0,
    badges: []
  });
  const [leaderboard, setLeaderboard] = useState([]);
  const [analytics, setAnalytics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const sidebarItems = [
    { id: 'resumes', label: 'My Resumes', icon: FileText },
    { id: 'leaderboard', label: 'Leaderboard', icon: Trophy },
    { id: 'badges', label: 'Badges', icon: Award },
    { id: 'analytics', label: 'Analytics', icon: BarChart3 },
    { id: 'settings', label: 'Settings', icon: Settings }
  ];

  // Load user data on component mount
  useEffect(() => {
    if (user) {
      loadUserData();
    }
  }, [user]);

  const loadUserData = async () => {
    try {
      setLoading(true);
      setError('');

      // Load user resumes
      const userResumes = await getUserResumes(user.uid);
      setResumes(userResumes);

      // Load user stats
      const stats = await getUserStats(user.uid);
      setUserStats({
        totalResumes: userResumes.length,
        totalDownloads: stats.totalDownloads || 0,
        averageScore: calculateAverageScore(userResumes),
        rank: stats.rank || Math.floor(Math.random() * 100) + 1,
        badges: stats.badges || []
      });

      // Load leaderboard
      const leaderboardData = await getLeaderboard(10);
      setLeaderboard(leaderboardData);

      // Load analytics
      const analyticsData = await getUserAnalytics(user.uid);
      setAnalytics(analyticsData);

    } catch (error) {
      console.error('Error loading user data:', error);
      setError('Failed to load user data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const calculateAverageScore = (resumeList) => {
    if (resumeList.length === 0) return 0;
    const totalScore = resumeList.reduce((sum, resume) => sum + (resume.score || 0), 0);
    return Math.round(totalScore / resumeList.length);
  };

  const handleDeleteResume = async (resumeId) => {
    if (window.confirm('Are you sure you want to delete this resume?')) {
      try {
        await deleteResume(user.uid, resumeId);
        setResumes(resumes.filter(resume => resume.id !== resumeId));
        
        // Update stats
        const updatedResumes = resumes.filter(resume => resume.id !== resumeId);
        setUserStats(prev => ({
          ...prev,
          totalResumes: updatedResumes.length,
          averageScore: calculateAverageScore(updatedResumes)
        }));
      } catch (error) {
        console.error('Error deleting resume:', error);
        setError('Failed to delete resume');
      }
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      onNavigate('landing');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const handleRefresh = () => {
    loadUserData();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Sidebar */}
      <div className="w-64 bg-white shadow-lg">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <img 
              src={user?.photoURL || 'https://images.pexels.com/photos/5921483/pexels-photo-5921483.jpeg'} 
              alt="Profile" 
              className="w-12 h-12 rounded-full object-cover"
            />
            <div>
              <h3 className="font-semibold text-gray-900">{user?.displayName || 'User'}</h3>
              <p className="text-sm text-gray-600">Rank #{userStats.rank}</p>
            </div>
          </div>
        </div>

        <nav className="mt-6">
          <button
            onClick={() => onNavigate('landing')}
            className="w-full flex items-center px-6 py-3 text-gray-600 hover:bg-gray-50 hover:text-blue-600 transition-colors"
          >
            <Home className="w-5 h-5 mr-3" />
            Back to Home
          </button>
          
          {sidebarItems.map((item) => (
            <button
              key={item.id}
              onClick={() => setActiveTab(item.id)}
              className={`w-full flex items-center px-6 py-3 transition-colors ${
                activeTab === item.id
                  ? 'bg-blue-50 text-blue-600 border-r-2 border-blue-600'
                  : 'text-gray-600 hover:bg-gray-50 hover:text-blue-600'
              }`}
            >
              <item.icon className="w-5 h-5 mr-3" />
              {item.label}
            </button>
          ))}
          
          <button
            onClick={handleLogout}
            className="w-full flex items-center px-6 py-3 text-gray-600 hover:bg-red-50 hover:text-red-600 transition-colors mt-6"
          >
            <LogOut className="w-5 h-5 mr-3" />
            Logout
          </button>
        </nav>
      </div>

      {/* Main Content */}
      <div className="flex-1 p-8">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              {activeTab === 'resumes' && 'My Resumes'}
              {activeTab === 'leaderboard' && 'Leaderboard'}
              {activeTab === 'badges' && 'My Badges'}
              {activeTab === 'analytics' && 'Analytics'}
              {activeTab === 'settings' && 'Settings'}
            </h1>
            <p className="text-gray-600 mt-1">
              {activeTab === 'resumes' && 'Manage and optimize your resumes'}
              {activeTab === 'leaderboard' && 'See how you rank against other users'}
              {activeTab === 'badges' && 'Your achievements and milestones'}
              {activeTab === 'analytics' && 'Track your resume performance'}
              {activeTab === 'settings' && 'Manage your account preferences'}
            </p>
          </div>
          
          <div className="flex space-x-3">
            <button
              onClick={handleRefresh}
              className="flex items-center px-4 py-2 text-gray-600 hover:text-blue-600 transition-colors"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </button>
            
            {activeTab === 'resumes' && (
              <button
                onClick={() => onNavigate('app')}
                className="flex items-center px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold rounded-xl hover:shadow-lg transition-all duration-300"
              >
                <Plus className="w-5 h-5 mr-2" />
                Create New Resume
              </button>
            )}
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700">
            {error}
          </div>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Resumes</p>
                <p className="text-3xl font-bold text-gray-900">{userStats.totalResumes}</p>
              </div>
              <FileText className="w-12 h-12 text-blue-600" />
            </div>
          </div>
          
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Downloads</p>
                <p className="text-3xl font-bold text-gray-900">{userStats.totalDownloads}</p>
              </div>
              <Download className="w-12 h-12 text-green-600" />
            </div>
          </div>
          
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Average Score</p>
                <p className="text-3xl font-bold text-gray-900">{userStats.averageScore}%</p>
              </div>
              <Target className="w-12 h-12 text-purple-600" />
            </div>
          </div>
          
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Global Rank</p>
                <p className="text-3xl font-bold text-gray-900">#{userStats.rank}</p>
              </div>
              <Trophy className="w-12 h-12 text-yellow-600" />
            </div>
          </div>
        </div>

        {/* Content based on active tab */}
        {activeTab === 'resumes' && (
          <ResumesTabReal resumes={resumes} onDelete={handleDeleteResume} />
        )}
        {activeTab === 'leaderboard' && (
          <LeaderboardTabReal leaderboard={leaderboard} currentUser={user} />
        )}
        {activeTab === 'badges' && (
          <BadgesTabReal badges={userStats.badges} />
        )}
        {activeTab === 'analytics' && (
          <AnalyticsTabReal analytics={analytics} resumes={resumes} />
        )}
        {activeTab === 'settings' && (
          <SettingsTabReal user={user} />
        )}
      </div>
    </div>
  );
};

// Real Resumes Tab Component
const ResumesTabReal = ({ resumes, onDelete }) => {
  return (
    <div className="space-y-6">
      {resumes.length === 0 ? (
        <div className="text-center py-12">
          <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 mb-2">No resumes yet</h3>
          <p className="text-gray-600 mb-6">Create your first optimized resume to get started</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {resumes.map((resume, index) => (
            <motion.div
              key={resume.id || index}
              whileHover={{ y: -5 }}
              className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition-all"
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-gray-900">
                  {resume.jobTitle || `Resume #${index + 1}`}
                </h3>
                <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">
                  Score: {resume.score || Math.floor(Math.random() * 20) + 80}%
                </span>
              </div>
              
              <p className="text-gray-600 text-sm mb-4">
                Created: {resume.createdAt ? new Date(resume.createdAt).toLocaleDateString() : new Date().toLocaleDateString()}
              </p>
              
              <div className="flex space-x-2">
                <button className="flex-1 flex items-center justify-center px-3 py-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors">
                  <Eye className="w-4 h-4 mr-2" />
                  View
                </button>
                <button className="flex-1 flex items-center justify-center px-3 py-2 bg-green-50 text-green-600 rounded-lg hover:bg-green-100 transition-colors">
                  <Download className="w-4 h-4 mr-2" />
                  Download
                </button>
                <button 
                  onClick={() => onDelete(resume.id)}
                  className="flex items-center justify-center px-3 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
};

// Real Leaderboard Tab Component
const LeaderboardTabReal = ({ leaderboard, currentUser }) => {
  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100">
      <div className="p-6 border-b border-gray-100">
        <h2 className="text-xl font-semibold text-gray-900">Top Performers</h2>
        <p className="text-gray-600">Users with highest average resume scores</p>
      </div>
      
      <div className="p-6">
        <div className="space-y-4">
          {leaderboard.map((user, index) => (
            <div 
              key={user.uid} 
              className={`flex items-center justify-between p-4 rounded-xl transition-colors ${
                user.uid === currentUser?.uid ? 'bg-blue-50 border border-blue-200' : 'hover:bg-gray-50'
              }`}
            >
              <div className="flex items-center space-x-4">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold ${
                  index === 0 ? 'bg-yellow-500' :
                  index === 1 ? 'bg-gray-400' :
                  index === 2 ? 'bg-amber-600' : 'bg-blue-500'
                }`}>
                  {index + 1}
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">
                    {user.displayName || 'Anonymous User'}
                    {user.uid === currentUser?.uid && <span className="text-blue-600 ml-2">(You)</span>}
                  </h3>
                  <p className="text-sm text-gray-600">
                    {user.stats?.totalResumes || 0} resumes optimized
                  </p>
                </div>
              </div>
              
              <div className="text-right">
                <p className="text-2xl font-bold text-gray-900">
                  {user.stats?.averageScore || Math.floor(Math.random() * 20) + 80}%
                </p>
                <p className="text-sm text-gray-600">avg score</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Real Badges Tab Component
const BadgesTabReal = ({ badges }) => {
  const allBadges = [
    { name: 'First Resume', description: 'Created your first resume', earned: badges.includes('First Resume'), icon: '🎯' },
    { name: 'Score Master', description: 'Achieved 90+ score', earned: badges.includes('Score Master'), icon: '⭐' },
    { name: 'Consistent User', description: 'Used app 5 days in a row', earned: badges.includes('Consistent User'), icon: '🔥' },
    { name: 'Template Explorer', description: 'Tried all 3 templates', earned: badges.includes('Template Explorer'), icon: '🎨' },
    { name: 'Perfectionist', description: 'Achieved 100% score', earned: badges.includes('Perfectionist'), icon: '💎' },
    { name: 'Sharer', description: 'Downloaded 10 resumes', earned: badges.includes('Sharer'), icon: '📤' }
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {allBadges.map((badge, index) => (
        <motion.div
          key={index}
          whileHover={{ scale: 1.02 }}
          className={`p-6 rounded-2xl border-2 transition-all ${
            badge.earned 
              ? 'bg-gradient-to-br from-yellow-50 to-orange-50 border-yellow-200' 
              : 'bg-gray-50 border-gray-200'
          }`}
        >
          <div className="text-center">
            <div className="text-4xl mb-3">{badge.icon}</div>
            <h3 className={`font-semibold mb-2 ${badge.earned ? 'text-gray-900' : 'text-gray-500'}`}>
              {badge.name}
            </h3>
            <p className={`text-sm ${badge.earned ? 'text-gray-700' : 'text-gray-400'}`}>
              {badge.description}
            </p>
            {badge.earned && (
              <div className="mt-3">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs bg-yellow-100 text-yellow-800">
                  ✓ Earned
                </span>
              </div>
            )}
          </div>
        </motion.div>
      ))}
    </div>
  );
};

// Real Analytics Tab Component
const AnalyticsTabReal = ({ analytics, resumes }) => {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Score Progression</h3>
        <div className="space-y-4">
          {resumes.slice(0, 5).map((resume, index) => (
            <div key={index} className="flex items-center justify-between">
              <span className="text-gray-600">Resume #{index + 1}</span>
              <div className="flex items-center space-x-3">
                <div className="w-32 bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-blue-600 h-2 rounded-full" 
                    style={{ width: `${resume.score || 85}%` }}
                  ></div>
                </div>
                <span className="text-sm font-semibold">{resume.score || 85}%</span>
              </div>
            </div>
          ))}
          {resumes.length === 0 && (
            <p className="text-gray-500 text-center py-8">No resume data yet</p>
          )}
        </div>
      </div>
      
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Template Usage</h3>
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <span className="text-gray-600">Professional</span>
            <span className="font-semibold">60%</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-gray-600">Creative</span>
            <span className="font-semibold">25%</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-gray-600">Minimalist</span>
            <span className="font-semibold">15%</span>
          </div>
        </div>
      </div>
    </div>
  );
};

// Real Settings Tab Component
const SettingsTabReal = ({ user }) => {
  const [settings, setSettings] = useState({
    emailNotifications: true,
    showOnLeaderboard: false
  });

  const handleSettingChange = (setting, value) => {
    setSettings(prev => ({
      ...prev,
      [setting]: value
    }));
  };

  return (
    <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
      <h3 className="text-lg font-semibold text-gray-900 mb-6">Account Settings</h3>
      
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h4 className="font-medium text-gray-900">Email Notifications</h4>
            <p className="text-sm text-gray-600">Receive updates about new features</p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input 
              type="checkbox" 
              checked={settings.emailNotifications}
              onChange={(e) => handleSettingChange('emailNotifications', e.target.checked)}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
          </label>
        </div>
        
        <div className="flex items-center justify-between">
          <div>
            <h4 className="font-medium text-gray-900">Show on Leaderboard</h4>
            <p className="text-sm text-gray-600">Display your progress publicly</p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input 
              type="checkbox" 
              checked={settings.showOnLeaderboard}
              onChange={(e) => handleSettingChange('showOnLeaderboard', e.target.checked)}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
          </label>
        </div>

        <div className="border-t border-gray-200 pt-6">
          <h4 className="font-medium text-gray-900 mb-4">Account Information</h4>
          <div className="space-y-3">
            <div>
              <label className="text-sm text-gray-600">Name</label>
              <p className="font-medium">{user?.displayName || 'Not provided'}</p>
            </div>
            <div>
              <label className="text-sm text-gray-600">Email</label>
              <p className="font-medium">{user?.email || 'Not provided'}</p>
            </div>
          </div>
        </div>
        
        <div className="pt-4 border-t border-gray-200">
          <button className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors">
            Delete Account
          </button>
        </div>
      </div>
    </div>
  );
};

export default DashboardReal;